<script setup>
import { ref, computed, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import SiteAdminService from '@/services/SiteAdminService'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'
import { ACCESS_LABELS, itemRef, parseRef, planCtaLabel } from '@/Utils/siteAccess'
import { contentKindLabel } from '@/Utils/siteContentKind'
import { PERIOD_OPTIONS } from '@/Utils/sitePeriod'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormTextarea from '@/components/Atoms/FormTextarea.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormCheckbox from '@/components/Atoms/FormCheckbox.vue'
import Alert from '@/components/Atoms/Alert.vue'
import SiteStripeConnect from './SiteStripeConnect.vue'

// Create / edit a paywall site product ("plan"). scope `site` = all-access;
// scope `items` = covers the mapped content items (`item:<id>` refs only —
// access model v2; castr answers 400 `category_scope_removed` for anything
// else). castr writes the paywall product and mirrors the mapping to the
// runtime.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	content: {
		type: Array,
		default: () => [],
	},
	// Stripe Connect gate: a priced plan needs an active account (free plans don't).
	canMonetize: {
		type: Boolean,
		default: true,
	},
	stripe: {
		type: Object,
		default: null,
	},
	// castr `GET /plans` → `capabilities` (§6h). `recurring: false` / absent:
	// subscriptions are deferred, only one-time plans can be created.
	capabilities: {
		type: Object,
		default: null,
	},
})
const emit = defineEmits(['saved', 'stripe-connected', 'items-changed'])
const { notify } = useNotification()

const isVisible = ref(false)
const saving = ref(false)
const error = ref(null)
const editing = ref(null) // product being edited (null = create)
// Opened from a content item that does not exist yet: the item is linked
// to the plan by the content modal right after it is created.
const forPendingItem = ref(false)
const form = ref(blank())
// Trello nk6mbl30: a plan does not change what it unlocks — an item that is
// Public (or Login required) today stays watchable for free even when it is
// ticked here. Say so, and offer to set those items to Paid with the save.
// Off when the content / access modal opened this one: that modal applies the
// access change itself once the plan exists.
const makePaid = ref(true)
const accessManaged = ref(false)

function blank(presetRefs = []) {
	return {
		title: '',
		description: '',
		scope: presetRefs.length ? 'items' : 'site',
		price: '',
		currency: 'USD',
		recurring: false,
		period: 'month',
		items: [...presetRefs],
	}
}

// Plan type (#32). Recurring plans are sold with a "Subscribe" button,
// one-time plans with "Buy" (free → "Unlock") — see planCtaLabel.
// TODO(donation): a "Donation / pay what you want" type needs paywall support
// (variable amount products) before it can be offered here.
const TYPE_OPTIONS = [
	{ value: 'one_time', text: 'One-time purchase' },
	{ value: 'recurring', text: 'Recurring subscription' },
]
const recurringAvailable = computed(() => props.capabilities?.recurring === true)
// An existing subscription keeps working while the flag is off (castr leaves
// it untouched); its type is shown read-only instead of being offered.
const typeLocked = computed(() => !recurringAvailable.value && Boolean(editing.value?.recurring))
// The dropdown only renders while subscriptions are enabled; otherwise the type is a static line.
const typeOptions = TYPE_OPTIONS
const planType = computed({
	get: () => (form.value.recurring ? 'recurring' : 'one_time'),
	set: (v) => { form.value.recurring = v === 'recurring' && (recurringAvailable.value || typeLocked.value) },
})
const periodLabel = computed(() => PERIOD_OPTIONS.find((o) => o.value === form.value.period)?.text || form.value.period)
const ctaPreview = computed(() => planCtaLabel({ recurring: form.value.recurring, price: priceNumber.value }))

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'BRL', 'MXN', 'INR', 'JPY', 'SGD', 'HKD', 'ZAR']
const currencyOptions = CURRENCIES.map((c) => ({ value: c, text: c }))
const periodOptions = PERIOD_OPTIONS
const scopeOptions = [
	{ value: 'site', text: 'All access — every item on the site' },
	{ value: 'items', text: 'Selected content items' },
]

const selectedRefs = computed(() => new Set(form.value.items))
// Refs castr stripped from an older plan (`legacy_category_refs`): the plan
// still unlocks them at the paywall until it is re-mapped to items here.
const legacyRefs = computed(() => (editing.value?.legacy_category_refs || []).length)
const toggleRef = (ref, on) => {
	const set = new Set(form.value.items)
	if (on) set.add(ref)
	else set.delete(ref)
	form.value.items = [...set]
}

const priceNumber = computed(() => Number(String(form.value.price).replace(',', '.')))
// Only money needs Stripe; a price of 0 is a free (registration) plan.
const needsStripe = computed(() => Number.isFinite(priceNumber.value) && priceNumber.value > 0 && !props.canMonetize)
const canSubmit = computed(() =>
	form.value.title.trim().length > 0
	&& Number.isFinite(priceNumber.value) && priceNumber.value >= 0
	&& (form.value.scope === 'site' || form.value.items.length > 0 || forPendingItem.value)
	&& !needsStripe.value
	&& !saving.value
)

const accessOf = (item) => item?.access || 'public'
const accessLabel = (item) => ACCESS_LABELS[accessOf(item)] || ACCESS_LABELS.public
/** Ticked items that are not Paid today — the plan lists them but viewers still watch them for free. */
const openItems = computed(() => (form.value.scope === 'items' && !accessManaged.value
	? props.content.filter((item) => selectedRefs.value.has(itemRef(item.id)) && accessOf(item) !== 'paid')
	: []))
const openTitles = computed(() => openItems.value.map((item) => `“${item.title}”`).join(', '))
const openNotice = computed(() => {
	const n = openItems.value.length
	if (!n) return ''
	const levels = [...new Set(openItems.value.map(accessLabel))].join(' / ')
	return n === 1
		? `${openTitles.value} is ${levels} today — viewers can watch it without buying this plan.`
		: `${n} ticked items are ${levels} today — viewers can watch them without buying this plan.`
})
// castr refuses `access: paid` without a connected Stripe account.
const canMakePaid = computed(() => props.canMonetize)
const willMakePaid = computed(() => openItems.value.length > 0 && canMakePaid.value && makePaid.value)

const summary = computed(() => {
	if (form.value.scope === 'site') return 'Unlocks everything on the site.'
	const items = form.value.items.map(parseRef).filter((r) => r?.kind === 'item').length
	const label = items ? `${items} ${items === 1 ? 'item' : 'items'}` : ''
	if (forPendingItem.value) {
		return label
			? `Unlocks ${label} plus the item you are adding.`
			: 'Unlocks the item you are adding (linked as soon as it is created).'
	}
	return label ? `Unlocks ${label}.` : 'Pick at least one content item.'
})

/**
 * @param {object|null} product   existing product to edit
 * @param {string[]} presetRefs   refs preselected when creating from a content row
 * @param {{ forItem?: boolean, accessManaged?: boolean }} opts
 *   forItem: the plan is for a content item that is not created yet;
 *   accessManaged: the caller sets the items' access itself (no Paid offer here)
 */
const show = (product = null, presetRefs = [], opts = {}) => {
	editing.value = product
	error.value = null
	forPendingItem.value = !product && Boolean(opts.forItem)
	accessManaged.value = Boolean(opts.accessManaged) || forPendingItem.value
	if (product) {
		form.value = {
			title: product.title || '',
			description: product.description || '',
			scope: product.scope === 'site' ? 'site' : 'items',
			price: product.price ?? '',
			currency: (product.currency || 'USD').toUpperCase(),
			recurring: Boolean(product.recurring),
			period: product.period || 'month',
			// item refs only — anything else would be refused by castr
			items: (product.items || []).filter((ref) => parseRef(ref)?.kind === 'item'),
		}
	} else {
		form.value = blank(presetRefs)
		if (forPendingItem.value) form.value.scope = 'items'
	}
	makePaid.value = true
	isVisible.value = true
}
const close = () => { isVisible.value = false }

watch(isVisible, (open) => { if (!open) { editing.value = null; error.value = null; forPendingItem.value = false } })

/**
 * Set the ticked Public / Login-required items to Paid. One PATCH per item,
 * `access` only: castr has already mirrored the saved plan into the runtime
 * mapping, and a Public → Paid patch without `planIds` keeps that mapping
 * (the local `planIds` may be stale, so it is never sent). Only when the
 * answer shows the plan missing is the mapping completed from the fresh
 * list. The plan itself is saved at this point, so failures are reported
 * without undoing it.
 */
async function setOpenItemsPaid(product) {
	const planId = product?.public_id
	const changed = []
	const failed = []
	for (const item of openItems.value) {
		try {
			let updated = { ...item, access: 'paid', ...((await SiteAdminService.updateContent(props.siteId, item.id, { access: 'paid' }))?.item || {}) }
			const planIds = updated.planIds || []
			if (planId && !planIds.includes(planId)) {
				const res = await SiteAdminService.updateContent(props.siteId, item.id, { planIds: [...planIds, planId] })
				updated = { ...updated, planIds: [...planIds, planId], ...(res?.item || {}) }
			}
			changed.push({ ...updated, access: 'paid' })
		} catch (err) {
			failed.push({ item, error: err })
		}
	}
	if (changed.length) emit('items-changed', changed)
	return { changed, failed }
}

const save = async () => {
	if (!canSubmit.value) return
	saving.value = true
	error.value = null
	const payload = {
		title: form.value.title.trim(),
		description: form.value.description.trim() || undefined,
		scope: form.value.scope,
		items: form.value.scope === 'items' ? form.value.items : undefined,
		price: priceNumber.value,
		currency: form.value.currency,
		recurring: form.value.recurring,
		period: form.value.recurring ? form.value.period : undefined,
	}
	try {
		const product = editing.value
			? await SiteAdminService.updatePlan(props.siteId, editing.value.public_id, payload)
			: await SiteAdminService.createPlan(props.siteId, payload)
		if (!editing.value) {
			trackSites(SITES_EVENTS.PLAN_CREATED, props.siteId, {
				scope: payload.scope,
				price: payload.price,
				currency: payload.currency,
				recurring: Boolean(payload.recurring),
				period: payload.period || null,
			})
		}
		const paid = willMakePaid.value ? await setOpenItemsPaid(product) : null
		const done = editing.value ? 'Plan updated.' : 'Plan created.'
		if (paid?.failed.length) {
			notify({ group: 'error', text: `${done} ${paid.changed.length} ${paid.changed.length === 1 ? 'item' : 'items'} set to Paid, ${paid.failed.length} could not be changed — set ${paid.failed.length === 1 ? 'its' : 'their'} access under Content › Library.` })
		} else if (paid?.changed.length) {
			notify({ group: 'success', text: `${done} ${paid.changed.length} ${paid.changed.length === 1 ? 'item is' : 'items are'} now Paid.` })
		} else {
			notify({ group: 'success', text: done })
		}
		emit('saved', product)
		isVisible.value = false
	} catch (err) {
		if (err.reason === 'stripe_not_connected') {
			error.value = 'Stripe is not connected yet — connect it below, then save the plan again.'
		} else if (err.reason === 'recurring_plans_unavailable') {
			error.value = 'Subscriptions are not available yet — save it as a one-time purchase.'
		} else {
			error.value = err.message || 'Could not save the plan.'
		}
	}
	saving.value = false
}

defineExpose({ show, close })
</script>

<template>
	<ModalBox v-model="isVisible" :title="editing ? 'Edit plan' : 'New plan'" size="sm" scroll-body wrapper-classes="lg:h-auto lg:max-h-[90vh]">
		<form class="flex flex-col gap-4 px-4 pb-4 sm:px-6 sm:pb-6" @submit.prevent="save">
			<Alert v-if="error" type="error" class="!mb-0">{{ error }}</Alert>

			<FormInput id="plan-title" v-model="form.title" label="Plan name" placeholder="e.g. All-access pass" input-classes="placeholder:text-surface-6" :maxlength="191" />
			<FormTextarea id="plan-description" v-model="form.description" label="Description (optional)" placeholder="e.g. Every live show plus the archive" :maxlength="500" />

			<!-- Price and currency on one row, labels on the same baseline; the currency stays compact. -->
			<div class="grid grid-cols-[minmax(0,1fr)_7.5rem] items-start gap-3">
				<FormInput id="plan-price" v-model="form.price" label="Price" placeholder="e.g. 9.99" input-classes="placeholder:text-surface-6" type="text" inputmode="decimal" />
				<FormSelect id="plan-currency" v-model="form.currency" label="Currency" :options="currencyOptions" />
			</div>
			<SiteStripeConnect v-if="needsStripe" compact :stripe="stripe || {}" action="sell a paid plan" @connected="emit('stripe-connected')" />
			<p v-else-if="!canMonetize" class="mb-0 -mt-2 text-tiny text-surface-7">Free plans (price 0) work without Stripe.</p>

			<div class="rounded-lg bg-surface-3 px-3 py-2.5" data-testid="plan-type-block">
				<!-- Subscriptions enabled: pick the type (and the interval for a subscription). -->
				<div v-if="recurringAvailable" class="grid gap-3" :class="form.recurring ? 'grid-cols-2' : 'grid-cols-1'">
					<FormSelect id="plan-type" v-model="planType" label="Plan type" :options="typeOptions" />
					<FormSelect v-if="form.recurring" id="plan-period" v-model="form.period" label="Billing interval" :options="periodOptions" />
				</div>
				<!-- Subscriptions deferred: only one-time plans can be created, so no dropdown.
					An existing subscription keeps its type and interval, read-only. -->
				<dl v-else class="mb-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm" data-testid="plan-type-static">
					<dt class="text-tiny font-medium text-surface-text-1">Plan type</dt>
					<dd class="mb-0 text-surface-text-2" data-testid="plan-type-value">{{ typeLocked ? 'Recurring subscription' : 'One-time purchase' }}</dd>
					<template v-if="typeLocked">
						<dt class="text-tiny font-medium text-surface-text-1">Billing interval</dt>
						<dd class="mb-0 text-surface-text-2" data-testid="plan-period-value">{{ periodLabel }}</dd>
					</template>
				</dl>
				<p class="mb-0 mt-2 text-tiny text-surface-7">
					<span v-if="typeLocked" class="block mb-0.5" data-testid="plan-type-locked">Existing subscription — its type can’t be changed.</span>
					{{ form.recurring ? 'Viewers are charged every interval until they cancel.' : 'One payment, lifetime access unless you set an access window later.' }}
					<span class="block mt-0.5" data-testid="plan-cta-preview">Viewers see a <span class="font-medium text-surface-8">“{{ ctaPreview }}”</span> button for this plan.</span>
				</p>
			</div>

			<FormSelect id="plan-scope" v-model="form.scope" label="What it unlocks" :options="scopeOptions" />

			<div v-if="form.scope === 'items'" class="rounded-lg bg-surface-3 p-3 max-h-64 overflow-y-auto" data-testid="plan-items">
				<div v-if="content.length" class="mb-1.5 flex items-baseline justify-between gap-2">
					<p class="mb-0 text-tiny font-medium text-surface-8">Content items</p>
					<p class="mb-0 text-tiny text-surface-7">Who can watch today</p>
				</div>
				<label v-for="item in content" :key="`i-${item.id}`" class="flex items-center gap-2 py-1 text-sm text-surface-text-1">
					<FormCheckbox :name="`plan-item-${item.id}`" :model-value="selectedRefs.has(itemRef(item.id))"
						@update:model-value="toggleRef(itemRef(item.id), $event)" />
					<span class="truncate">{{ item.title }}</span>
					<span class="ml-auto shrink-0 text-tiny text-surface-7">{{ contentKindLabel(item) }}</span>
					<span class="shrink-0 rounded px-1.5 py-px text-[11px] font-medium" :class="accessOf(item) === 'paid' ? 'bg-main-transparent text-main-light' : 'bg-surface-4 text-surface-8'" :data-testid="`plan-item-access-${item.id}`">{{ accessLabel(item) }}</span>
				</label>
				<p v-if="!content.length" class="mb-0 text-tiny text-surface-7">
					{{ forPendingItem ? 'No other items yet — the item you are adding is linked automatically.' : 'Add content first.' }}
				</p>
			</div>
			<div v-if="openItems.length" class="rounded-md bg-warning/10 px-3 py-2.5 text-tiny text-warning" data-testid="plan-open-items">
				<p class="mb-1.5">{{ openNotice }}</p>
				<FormCheckbox v-if="canMakePaid" name="plan-make-paid" :input-value="true" :model-value="makePaid" class="!items-start" data-testid="plan-make-paid" @update:model-value="makePaid = $event">
					<template #label>
						Set {{ openItems.length === 1 ? 'it' : 'them' }} to <span class="font-medium">Paid</span> when I save
						<span class="mt-0.5 block text-tiny text-surface-7" data-testid="plan-make-paid-hint">{{ makePaid ? 'Viewers will then need this plan or an all-access plan to watch.' : 'Left as is: the plan lists the content, but it stays free to watch until its access is changed under Content › Library.' }}</span>
					</template>
				</FormCheckbox>
				<p v-else class="mb-0 text-surface-7">Paid access needs a connected Stripe account — until then the content stays free to watch. Connect Stripe under Settings › General.</p>
			</div>
			<p v-if="legacyRefs" class="mb-0 rounded-md bg-warning/10 px-3 py-2 text-tiny text-warning" data-testid="plan-legacy-refs">
				This plan was mapped to {{ legacyRefs }} {{ legacyRefs === 1 ? 'category' : 'categories' }} before. Plans now unlock content items only — tick the items it should cover and save to re-map it.
			</p>
			<p class="mb-0 text-tiny text-surface-7">{{ summary }}</p>
		</form>

		<template #footer>
			<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
				<FormButton class="w-full justify-center sm:w-auto" :disabled="!canSubmit" :is-loading="saving" @click="save">
					{{ editing ? 'Save plan' : 'Create plan' }}
				</FormButton>
				<FormButton class="w-full justify-center sm:w-auto" type="secondary" @click="close">Cancel</FormButton>
			</footer>
		</template>
	</ModalBox>
</template>
